export { OverviewPeriodPreset as FinanceOverviewDatePreset } from "@/shared/model/overview-period"

import { Currency } from "@ibada/shared-types/finance"
import { BranchType } from "@ibada/shared-types/common"

export { Currency }

import { type OverviewContext, type OverviewGranularity, type OverviewSurface } from "@/features/finance/model/finance-normalized-overview"

/**
 * IBA-114 — there is ONE currency value set, and it lives in shared-types so the API and both frontends cannot
 * drift apart. `FinanceCurrency` and `CurrencyCode` were two separate enums in this very file with the same four
 * members, declared in different orders, both in use. They are now aliases of the shared `Currency`, so a value
 * from the API, a value from a form and a value from a record all have the same identity.
 */
export const FinanceCurrency = Currency
export type FinanceCurrency = Currency

/**
 * Money, as it arrives from the API and as it reaches the renderer: a canonical DECIMAL STRING at the
 * currency's scale (IBA-116) — `"1500.00"` in USD, `"1500"` in RWF. The `currency` travels with it.
 *
 * It is a `string` and not a `number` on purpose. IEEE-754 cannot hold most decimal fractions, so a float is a
 * lossy container for an amount that has to reconcile with a bank statement: `Number("9007199254740993.01")`
 * quietly drops the cent, with no error anywhere. `Intl.NumberFormat.format()` parses the string EXACTLY, so
 * the browser needs no decimal library to *show* money correctly — it only ever needed to stop converting it
 * to a float first.
 *
 * **The frontend displays money; it never computes it.** Do not sum, subtract or round these — if you need a
 * total, the server owes you one (see `totals`, `accounts.totals`, `summary.totalCash`, `/finance/overview/health`).
 * To compare or sort, use `compareMoney` from `@ibada/shared-types/finance`; for chart geometry only, use
 * `moneyToNumberForChartGeometryOnly`.
 */
export type MoneyText = string
export type FinanceAccount = {
  id: string
  name: string
  type: FinanceAccountType
  currency: FinanceCurrency
}

/** Mirrors Prisma `FinanceAccountType` (IBA-114) — how a finance account physically holds money. */
export enum FinanceAccountType {
  Bank = "BANK",
  Cash = "CASH",
  MobileMoney = "MOBILE_MONEY",
}
import { RecordAvailability } from "@ibada/shared-types/common"

export { RecordAvailability }

import {
  ActivityParticipantRole,
  FinanceActivityStatus,
  FinancePaymentMethod,
  FinanceTransactionKind,
  FinanceTransactionStatus,
  FinanceTransactionType,
  PledgeContributorType,
  PledgeStatus,
  ReceiptTemplateKey,
} from "@ibada/shared-types/finance"

export {
  ActivityParticipantRole,
  FinanceActivityStatus,
  FinancePaymentMethod,
  FinanceTransactionKind,
  FinanceTransactionStatus,
  FinanceTransactionType,
  PledgeContributorType,
  PledgeStatus,
  ReceiptTemplateKey,
}

/**
 * A context row a branch can choose to print on its receipts.
 *
 * Only CONTEXT — the amount, receipt number, date, payer and payment method always print and are deliberately
 * not selectable. What varies is how much internal structure a church puts on a document it hands to a member:
 * some want the fund and activity named, others treat the group or ministry someone belongs to as private.
 */
export enum ReceiptDetailField {
  Pledge = "pledge",
  Activity = "activity",
  Category = "category",
  Fund = "fund",
  Group = "group",
  Ministry = "ministry",
  Workspace = "workspace",
  Branch = "branch",
}

/** The three layouts a branch can choose, in the order they are offered. */
export const RECEIPT_TEMPLATE_KEYS: readonly ReceiptTemplateKey[] = [
  ReceiptTemplateKey.Classic,
  ReceiptTemplateKey.Compact,
  ReceiptTemplateKey.Formal,
]

/** Canonical order — also the order they print in, so it never depends on click order. */
export const RECEIPT_DETAIL_FIELDS: readonly ReceiptDetailField[] = [
  ReceiptDetailField.Pledge,
  ReceiptDetailField.Activity,
  ReceiptDetailField.Category,
  ReceiptDetailField.Fund,
  ReceiptDetailField.Group,
  ReceiptDetailField.Ministry,
  ReceiptDetailField.Workspace,
  ReceiptDetailField.Branch,
]

export type ReceiptConfig = {
  templateKey: ReceiptTemplateKey
  /** Absent = print every row, which is what receipts issued before this setting existed already do. */
  detailFields?: ReceiptDetailField[] | null
  organizationNameOverride?: string | null
  branchNameOverride?: string | null
  headerSummary?: string | null
  notesText?: string | null
  footerText?: string | null
  footerContacts?: string | null
  accentColor?: string | null
  signatureLabel?: string | null
  signedByName?: string | null
  signedByTitle?: string | null
  signatureSvg?: string | null
}

export type ReceiptConfigDefaults = {
  organizationName: string
  branchName: string
  headerSummary: string
  footerContacts: string
}

export type ReceiptConfigPayload = {
  config: ReceiptConfig
  defaults: ReceiptConfigDefaults
}

export type ChurchFinanceTransaction = {
  id: string
  type: FinanceTransactionType
  categoryId: string
  amount: number
  currency: FinanceCurrency
  date: string
  note?: string
}

/**
 * The transfer a transaction is one half of, stated from THIS transaction's side.
 *
 * `direction` and `counterpartBranch` are resolved by the backend per side — the source leg sent money TO the
 * counterpart, the destination leg received it FROM them — so no consumer has to work out which end it is
 * holding. Null on an ordinary income or expense transaction.
 */
export type FinanceTransactionTransferContext = {
  id: string
  status: string
  transferReference: string | null
  direction: TransferLegDirection
  counterpartBranch: { id: string; name: string; slug: string; branchType: BranchType } | null
  createdAt: string
  submittedAt: string | null
  approvedAt: string | null
  rejectedAt: string | null
  cancelledAt: string | null
  createdByUser: FinanceTransactionUserSummary | null
  /** Present on both halves of a transfer; null otherwise. */
  transfer?: FinanceTransactionTransferContext | null
  submittedByUser: FinanceTransactionUserSummary | null
  approvedByUser: FinanceTransactionUserSummary | null
  rejectedByUser: FinanceTransactionUserSummary | null
  cancelledByUser: FinanceTransactionUserSummary | null
}

/** Which end of a transfer a given transaction sits on. */
export enum TransferLegDirection {
  In = "IN",
  Out = "OUT",
}

export enum TransactionDirection {
  Income = "INCOME",
  Expense = "EXPENSE",
  TransferOut = "TRANSFER_OUT",
  TransferIn = "TRANSFER_IN",
  /**
   * A same-branch account-to-account move. As a LIST FILTER the API widens it to every transfer — both legs
   * of a cross-context movement included — so "show me transfers" returns money sent and received alike.
   */
  Transfer = "TRANSFER",
}

export enum TransactionStatus {
  Posted = "POSTED",
  Voided = "VOIDED",
}

export enum TransactionStatusFilter {
  All = "ALL",
  Posted = "POSTED",
  Voided = "VOIDED",
}

export enum TransactionSource {
  Manual = "MANUAL",
}

export enum TransactionViewTab {
  All = "ALL",
  Income = "INCOME",
  Expenses = "EXPENSES",
  /** Both legs of a movement between contexts, plus same-branch account-to-account moves. */
  Transfers = "TRANSFERS",
}

export enum FinanceTransactionStatusFilter {
  All = "ALL",
  Active = "ACTIVE",
  Voided = "VOIDED",
  Cancelled = "CANCELLED",
}

/** Status filter for the finance directory list tables (accounts, income/expense categories). */
export enum FinanceDirectoryStatusFilter {
  Active = "ACTIVE",
  Archived = "ARCHIVED",
  All = "ALL",
}

export enum FinanceCategoryScope {
  Branch = "BRANCH",
  Parish = "PARISH",
  Shared = "SHARED",
}

export type FinanceIncomeCategorySummary = {
  id: string
  name: string
  branchId: string | null
  isParishOnly: boolean
  scope?: FinanceCategoryScope
}

export type FinanceExpenseCategorySummary = {
  id: string
  name: string
  branchId: string | null
  scope?: FinanceCategoryScope
}

export type FinanceTransactionActivitySummary = {
  /** A FinanceActivity id. NOT usable against /activities — see `activityId`. */
  id: string
  /**
   * The operational Activity this finance record bridges to (ADR-0017), or null for a legacy standalone
   * finance activity that has no operational counterpart. This is the only id that may be used to link to
   * `/activities/:id`; using `id` there produces a 404, because the two live in different tables.
   */
  activityId: string | null
  title: string
  status: FinanceActivityStatus
  branchId: string | null
  incomeCategoryId: string | null
  fundId: string | null
  startsAt: string
  endsAt: string
}

export type FinanceTransactionBranchSummary = {
  id: string
  name: string
  slug: string
  status: string
}

export type FinanceTransactionMemberSummary = {
  id: string
  firstName: string
  middleName: string | null
  lastName: string
}

export type FinanceTransactionGroupSummary = {
  id: string
  name: string
}

export type FinanceTransactionMinistrySummary = {
  id: string
  name: string
}

export type FinanceAccountBranchSummary = {
  id: string
  workspaceId: string
  name: string
  slug: string
  status: string
}

export type FinanceAccountRecord = {
  id: string
  workspaceId: string
  branchId: string
  name: string
  description: string | null
  /** Optional last 4 digits of the real account number — a display-only reference aid, never used for matching. */
  accountNumberLast4: string | null
  /** How the money is physically held (IBA-114). A mobile-money wallet is not a bank account. */
  type: FinanceAccountType
  /** The currency the account HOLDS — inherited from the branch's accounting currency, never set directly. */
  currency: Currency
  status: "ACTIVE" | "ARCHIVED"
  scope: FinanceCategoryScope
  branch: FinanceAccountBranchSummary
  /** The account's ledger balance; present only when the accounts list is queried with includeBalances. */
  balance: MoneyText | null
  /** Opening balance (IBA-71): the set amount (null if none) and whether it is locked (posted transactions exist). */
  openingBalance: MoneyText | null
  openingBalanceLocked: boolean
  /**
   * The ledger account behind this account's balance — null until it has posted anything.
   *
   * It is what lets a balance link to the entries that produce it: the ledger is keyed by the ACCOUNTING
   * account, and this screen only ever knew the finance account.
   */
  accountingAccountId: string | null
  /** The Chart-of-Accounts code of that ledger account — null until the account has posted (no ledger identity yet). */
  accountingAccountCode: string | null
  hasLedgerActivity: boolean
  canEditFinancialDetails: boolean
  createdAt: string
  updatedAt: string
}

/**
 * IBA-154 (D9). A fund is either owned by an income category (created and maintained by the engine) or defined
 * by the church itself. Mirrors the backend `FundKind`.
 */
export enum FinanceFundKind {
  SystemLinked = "SYSTEM_LINKED",
  Custom = "CUSTOM",
}

/**
 * The lifecycle of a fund as STORED. Distinct from `FinanceFundListStatus`, which is the list endpoint's
 * filter and carries an extra `All` that no fund is ever in.
 */
export enum FinanceFundStatus {
  Active = "ACTIVE",
  Closed = "CLOSED",
  Archived = "ARCHIVED",
}

export type FinanceFundRecord = {
  id: string
  workspaceId: string
  branchId: string
  /** Display name, already in the reader's language: the API localizes a SYSTEM_LINKED fund (ADR-0030). */
  name: string
  /** For a SYSTEM_LINKED fund, the income account's translation key. Informational only: never look it up for
   *  display (ADR-0030). Null for CUSTOM funds. */
  nameTranslationKey: string | null
  description: string | null
  status: FinanceFundStatus
  /** IBA-154 (D9): SYSTEM_LINKED funds mirror an income category and are immutable; CUSTOM funds are the pots
   *  the church defines itself. Replaces `isDefault` — there is no General Fund. */
  kind: FinanceFundKind
  linkedIncomeCategoryId: string | null
  /** For a SYSTEM_LINKED fund, the income AccountingAccount this fund is bound to (the authoritative binding);
   *  null for CUSTOM funds. Lets the client tell which fund an income account's money lands in. */
  linkedIncomeAccountId: string | null
  scope: FinanceCategoryScope
  targetAmount: MoneyText | null
  startDate: string | null
  endDate: string | null
  /** Restricted Funds: spending-restriction config (allowed expense categories + date window). */
  isRestricted: boolean
  restrictionStartDate: string | null
  restrictionEndDate: string | null
  allowedExpenseAccountIds: string[]
  /**
   * The accounting currency of the branch that owns the fund — the SCALE of `targetAmount` and `balance`
   * (IBA-116). Null only if the API omitted it; the client denominates via `useBranchCurrency` then.
   */
  currency: CurrencyCode | null
  /** The fund's current balance (money tracked against the fund). */
  balance: MoneyText
  /** Held by pending workflows — a submitted transfer freezes the amount it promised. */
  reserved: MoneyText
  /**
   * balance − reserved: what the fund may still commit, and the number the spend gate enforces. A UI that
   * offers a percentage of `balance` can compute an amount that submission then refuses.
   */
  available: MoneyText
  /** Number of finance activities linked to this fund. */
  activityCount: number
  /**
   * The activity this fund is dedicated to (IBA-175), or null.
   *
   * The INVERSE of the link `activityCount` counts, and stricter. An activity's `fundId` is a preference —
   * the fund its transactions pre-select. A dedication says money may only move against that one activity,
   * and the API refuses a transaction naming another.
   */
  dedicatedActivityId: string | null
  dedicatedActivityName: string | null
  branch: FinanceAccountBranchSummary
  createdAt: string
  updatedAt: string
}

export type FinanceTransactionAccountSummary = {
  id: string
  branchId: string
  name: string
  status: "ACTIVE" | "ARCHIVED"
}

export type FinanceTransactionLine = {
  id: string
  accountId: string
  amount: MoneyText
  account: FinanceTransactionAccountSummary
  createdAt: string
  updatedAt: string
}

/** IBA-67 fund attribution line — how a transaction's amount is split across funds (read-only). */
export type FinanceTransactionFundAllocation = {
  fundId: string
  fundName: string
  fundNameTranslationKey: string | null
  amount: MoneyText
}

/** The user who recorded a transaction (attribution). */
export type FinanceTransactionUserSummary = {
  id: string
  email: string
  firstName: string
  lastName: string
  avatarUrl: string | null
}

export type FinanceTransactionRecord = {
  id: string
  workspaceId: string
  branchId: string
  transactionKind: FinanceTransactionKind
  accountId: string | null
  transactionReference: string | null
  type: TransactionDirection
  amount: MoneyText
  currency: CurrencyCode | null
  /**
   * categories→accounts: the income/expense AccountingAccount this transaction records against (the "why" of the
   * money). Replaces the legacy income/expense CATEGORY — a transaction now points at a first-class Chart-of-Accounts
   * income/expense account.
   */
  accountingAccountId: string | null
  activityId: string | null
  programId: string | null
  pledgeId: string | null
  memberId: string | null
  groupId: string | null
  ministryId: string | null
  paymentMethod: FinancePaymentMethod | null
  paymentReference: string | null
  description: string | null
  occurredAt: string
  status: FinanceTransactionStatus
  voidedAt: string | null
  cancelledAt: string | null
  cancelledByUserId: string | null
  cancelledBy: FinanceTransactionMemberSummary | null
  cancelledByTransactionId: string | null
  cancellationReason: string | null
  reversalTransactionId: string | null
  reversedTransactionId: string | null
  /**
   * The counterpart of a reversal pair, by REFERENCE — what this row undoes, and what undid it.
   *
   * A correction is two rows that a date sort rarely places together (and pagination may separate entirely),
   * so each has to be able to name the other on its own. An id cannot do that; "BUK-000308" can.
   */
  reversedTransactionReference: string | null
  reversalTransactionReference: string | null
  /**
   * categories→accounts: the income/expense AccountingAccount summary. `code` is the Chart-of-Accounts code, shown
   * as a muted prefix before the name (e.g. "701 Tithes"); it is optional so display degrades gracefully to the
   * name alone until the backend field lands.
   */
  /** `name` arrives localized by the API (ADR-0030); render it as received. */
  accountingAccount: { id: string; name: string; translationKey: string | null; code?: string } | null
  activity: FinanceTransactionActivitySummary | null
  branch: FinanceTransactionBranchSummary | null
  account: FinanceTransactionAccountSummary | null
  lines: FinanceTransactionLine[]
  fundAllocations: FinanceTransactionFundAllocation[]
  member: FinanceTransactionMemberSummary | null
  group: FinanceTransactionGroupSummary | null
  ministry: FinanceTransactionMinistrySummary | null
  /** The department a branch-owned transaction is credited to (attribution/source). */
  departmentSource: FinanceTransactionMinistrySummary | null
  pledge: {
    id: string
    status: "OPEN" | "PARTIALLY_FULFILLED" | "FULFILLED" | "CANCELLED" | "OVERDUE"
    amountPledged: MoneyText
    amountReceived: MoneyText
  } | null
  createdByUser: FinanceTransactionUserSummary | null
  /** Present on both halves of a transfer; null otherwise. */
  transfer?: FinanceTransactionTransferContext | null
  /**
   * Whether this transaction sits inside a reconciliation that has already been signed off (IBA-161).
   * Cancelling it is still permitted; the cancel wizard warns that it disturbs a certified period.
   */
  isInApprovedReconciliation?: boolean
  createdAt: string
  updatedAt: string
}

export type FinanceTransactionListQuery = {
  workspaceId: string
  /**
   * When true, reversal ledger rows are included. Default UX keeps this false.
   */
  includeReversals?: boolean
  /**
   * Transaction reads are always branch-scoped. Parish/consolidated finance views use their own endpoints and
   * disclosure rules; omitting this would make the list ambiguous under branch autonomy.
   */
  branchId: string
  /** Scope to a department's own transactions (department finance). */
  departmentId?: string
  // categories→accounts: filter by the income/expense AccountingAccount the transaction records against. The
  // backend list endpoint accepts this single param (the former income/expense category ids are retired).
  accountingAccountId?: string
  activityId?: string
  /** Scope to transactions with a fund-allocation line to this fund (the activity Finance tab). */
  fundId?: string
  pledgeId?: string
  groupId?: string
  ministryId?: string
  memberId?: string
  status?: FinanceTransactionStatus
  type?: TransactionDirection
  dateFrom?: string
  dateTo?: string
  search?: string
  page?: number
  limit?: number
  sortBy?: "occurredAt" | "createdAt" | "amount" | "type" | "status"
  sortOrder?: "asc" | "desc"
}

export type CreateFinanceTransactionInput = {
  workspaceId: string
  branchId: string
  /** Department ownership/contributor — set for a department-recorded transaction. */
  departmentId?: string | null
  /** The single financial account this income/expense affects (destination for income, source for expense). */
  accountId?: string | null
  /**
   * The fund an EXPENSE draws down (IBA-154 D9). Required for expenses — there is no General Fund to fall back
   * on, so which designation is being spent is always an explicit choice.
   *
   * Must be omitted for INCOME: income is designated by its category's linked fund, and sending a fund is
   * rejected (`INCOME_FUND_NOT_SELECTABLE`) rather than ignored.
   */
  fundId?: string | null
  /**
   * IBA-67 opt-in per-fund split of an INCOME amount (income only). Omitted ⇒ 100% of the income flows to the
   * income account's linked fund (the backend resolves it). When present, the lines must name distinct funds and
   * sum to `amount` — the server re-validates both and rejects a mismatch.
   */
  fundAllocations?: Array<{ fundId: string; amount: MoneyText }>
  /** Server-issued digest of the authoritative allocation-aware budget impact preview. */
  budgetImpactRevision?: string
  type: TransactionDirection
  /** A canonical decimal string (IBA-116) — money is never sent as a JSON number. */
  amount: MoneyText
  /**
   * categories→accounts: the authoritative income/expense AccountingAccount this transaction records against.
   * The normal form sends this; the legacy income/expense category ids remain accepted as a transitional bridge.
   */
  accountingAccountId?: string | null
  incomeCategoryId?: string | null
  expenseCategoryId?: string | null
  activityId?: string | null
  programId?: string | null
  pledgeId?: string | null
  memberId?: string | null
  groupId?: string | null
  departmentSourceId?: string | null
  ministryId?: string | null
  paymentMethod?: FinancePaymentMethod | null
  paymentReference?: string | null
  description?: string | null
  occurredAt?: string
}

/**
 * Internal account-to-account transfer create request (POST /finance/transfers). Moves money between two of the
 * branch's own financial accounts. No category, no fund, no fees — the amount reaches the destination exactly.
 */
export type CreateTransferInput = {
  workspaceId: string
  sourceAccountId: string
  destinationAccountId: string
  /** A canonical decimal string (IBA-116) — money is never sent as a JSON number. */
  amount: MoneyText
  occurredAt: string
  reference?: string | null
  description?: string | null
}

/** The transfer record returned by POST /finance/transfers (a single balanced TRANSFER-type transaction). */
export type FinanceTransferRecord = {
  id: string
  workspaceId: string
  branchId: string
  type: FinanceTransactionType
  transactionReference: string | null
  amount: MoneyText
  currency: CurrencyCode | null
  sourceAccountId: string
  destinationAccountId: string
  sourceAccount: FinanceTransactionAccountSummary | null
  destinationAccount: FinanceTransactionAccountSummary | null
  reference: string | null
  description: string | null
  occurredAt: string
  status: FinanceTransactionStatus
  transactionKind: FinanceTransactionKind
  journalEntryId: string | null
  createdAt: string
  updatedAt: string
}

export type { BudgetImpactPreviewRequest as BudgetLineMatchQuery } from "@ibada/shared-types/finance"

export type UpdateFinanceTransactionInput = {
  id: string
  patch: Partial<{
    branchId: string
    accountId: string | null
    type: TransactionDirection
    /** A canonical decimal string (IBA-116) — money is never sent as a JSON number. */
    amount: MoneyText
    incomeCategoryId: string | null
    expenseCategoryId: string | null
    activityId: string | null
    pledgeId: string | null
    memberId: string | null
    groupId: string | null
    ministryId: string | null
    paymentMethod: FinancePaymentMethod | null
    paymentReference: string | null
    description: string | null
    occurredAt: string
  }>
}

export type VoidFinanceTransactionInput = {
  id: string
  reason?: string | null
}

export type CancelFinanceTransactionInput = {
  id: string
  confirmationPhrase: string
  cancellationReason: string | null
}

/**
 * The only fields a posted transaction lets you change.
 *
 * Everything financial — amount, accounts, fund, date, branch — is immutable and the API answers 409 for it.
 * This exists for the ordinary case of a typo in a reference or a description that was never quite right, so
 * fixing one does not require reversing a transaction that was otherwise correct.
 */
export type UpdateFinanceTransactionMetadataInput = {
  id: string
  description?: string | null
  paymentMethod?: FinancePaymentMethod | null
  paymentReference?: string | null
}

export type FinanceActivity = {
  id: string
  /**
   * Operational Activity id bridged by this finance activity (ADR-0017).
   * Null only for legacy standalone finance activities.
   */
  activityId: string | null
  workspaceId: string
  branchId: string | null
  collectsFunds: boolean
  incomeCategoryId: string | null
  fundId: string | null
  groupId: string | null
  ministryId: string | null
  title: string
  description: string | null
  status: FinanceActivityStatus
  startsAt: string
  endsAt: string
  responsibleMemberId: string | null
  incomeCategory: FinanceIncomeCategorySummary | null
  responsibleMember: FinanceTransactionMemberSummary | null
  createdAt: string
  updatedAt: string
}

export type FinanceActivitiesListQuery = {
  workspaceId: string
  branchId?: string
  incomeCategoryId?: string
  fundId?: string
  groupId?: string
  ministryId?: string
  status?: FinanceActivityStatus
  activeOnly?: boolean
  page?: number
  limit?: number
}

export type CreateFinanceActivityInput = {
  workspaceId: string
  branchId?: string | null
  collectsFunds?: boolean
  incomeCategoryId?: string | null
  fundId?: string | null
  groupId?: string | null
  ministryId?: string | null
  title: string
  description?: string | null
  status?: FinanceActivityStatus
  startsAt: string
  endsAt: string
  responsibleMemberId?: string | null
}

export type UpdateFinanceActivityInput = {
  id: string
  patch: Partial<{
    branchId: string | null
    collectsFunds: boolean
    incomeCategoryId: string | null
    fundId: string | null
    groupId: string | null
    ministryId: string | null
    title: string
    description: string | null
    status: FinanceActivityStatus
    startsAt: string
    endsAt: string
    responsibleMemberId: string | null
  }>
}

export type FinanceOverviewBreakdownItem = {
  id: string
  name: string
  totalIncome: number
  totalExpense: number
  balance: number
  transactionCount: number
}

export type FinanceOverviewRecentTransaction = {
  id: string
  type: TransactionDirection
  amount: number
  occurredAt: string
  description: string | null
  branch: Pick<FinanceTransactionBranchSummary, "id" | "name"> | null
  incomeCategory: Pick<FinanceIncomeCategorySummary, "id" | "name"> | null
  expenseCategory: Pick<FinanceExpenseCategorySummary, "id" | "name"> | null
  activity: Pick<FinanceTransactionActivitySummary, "id" | "title"> | null
  group: FinanceTransactionGroupSummary | null
  ministry: FinanceTransactionMinistrySummary | null
  member: FinanceTransactionMemberSummary | null
}

export type FinanceOverviewActiveActivity = {
  id: string
  title: string
  startsAt: string | null
  endsAt: string | null
  incomeCategory: Pick<FinanceIncomeCategorySummary, "id" | "name"> | null
  responsibleMember: FinanceTransactionMemberSummary | null
  totalIncome: number
  totalExpense: number
  balance: number
  transactionCount: number
}

export type FinanceOverviewQuery = {
  workspaceId: string
  context?: OverviewContext
  profile?: OverviewSurface
  branchId?: string
  incomeCategoryId?: string
  expenseCategoryId?: string
  activityId?: string
  groupId?: string
  ministryId?: string
  memberId?: string
  dateFrom?: string
  dateTo?: string
  granularity?: OverviewGranularity
  limit?: number
}

export type ParishOverviewQuery = {
  workspaceId: string
  dateFrom?: string
  dateTo?: string
  trend?: "daily" | "monthly"
}

/** @deprecated Parish overview normalized via GET /finance/overview?context=parish — retained for transitional typing */
export type ParishOverviewSummary = {
  totalIncome: number
  totalExpense: number
  net: number
  transactionCount: number
}

export type ParishOverviewTrend = {
  period: string
  income: number
  expense: number
  net: number
  transactionCount: number
}

export type ParishOverviewCategoryBreakdown = {
  id: string
  name: string
  totalIncome: number
  totalExpense: number
  net: number
  transactionCount: number
}

export type ParishOverviewActivityBreakdown = {
  id: string
  name: string
  totalAmount: number
  transactionCount: number
}

export type ParishOverviewResponse = {
  workspaceId: string
  parishBranchId: string
  summary: ParishOverviewSummary
  trends: ParishOverviewTrend[]
  breakdowns: {
    incomeCategories: ParishOverviewCategoryBreakdown[]
    expenseCategories: ParishOverviewCategoryBreakdown[]
    activities: ParishOverviewActivityBreakdown[]
  }
}

export type ListParishTransactionsQuery = {
  workspaceId: string
  branchId?: string
  includeReversals?: boolean
  incomeCategoryId?: string
  expenseCategoryId?: string
  activityId?: string
  type?: TransactionDirection
  status?: FinanceTransactionStatus
  dateFrom?: string
  dateTo?: string
  search?: string
  page?: number
  limit?: number
  sortBy?: "occurredAt" | "createdAt" | "amount" | "type" | "status"
  sortOrder?: "asc" | "desc"
}

export type ListParishTransactionsResponse = {
  workspaceId: string
  parishBranchId: string
  items: ParishTransactionResponse[]
  total: number
  page: number
  limit: number
}

export type FinanceCurrentIncomeBalancesQuery = {
  workspaceId: string
  branchId: string
  incomeCategoryId?: string
  /** Scope to a department's own income-category balances (department finance). Omitted = branch-owned only. */
  departmentId?: string
}

export type FinanceCurrentIncomeBalanceQuery = {
  workspaceId: string
  branchId: string
  incomeCategoryId: string
}

export type FinanceCurrentIncomeBalanceItem = {
  incomeCategoryId: string
  incomeCategoryName: string
  incomeCategoryTranslationKey: string | null
  visibility: "SHARED" | "BRANCH"
  currentBalance: MoneyText
  spendableBalance: MoneyText
  totalIncome: MoneyText
  totalExpense: MoneyText
  hasBalanceRow: boolean
}

export type FinanceCurrentIncomeBalanceAccountBreakdown = {
  accountId: string
  accountName: string
  branchId: string
  status: "ACTIVE" | "ARCHIVED"
  isDefault: boolean
  currentBalance: MoneyText
  spendableBalance: MoneyText
  hasBalanceRow: boolean
}

/** The Soldes screen's totals. Every figure below is summed by the BACKEND over the whole branch (IBA-117). */
export type FinanceCurrentIncomeBalanceTotals = {
  totalIncome: MoneyText
  totalExpense: MoneyText
  currentBalance: MoneyText
}

export type FinanceAccountBalanceItem = {
  accountId: string
  accountName: string
  currentBalance: MoneyText
}

export type FinanceCategoryAccountBreakdownItem = {
  accountId: string
  accountName: string
  income: MoneyText
  expense: MoneyText
  balance: MoneyText
}

export type FinanceCategoryAccountBreakdown = {
  incomeCategoryId: string
  items: FinanceCategoryAccountBreakdownItem[]
  totals: { income: MoneyText; expense: MoneyText; balance: MoneyText }
}

export type FinanceCurrentIncomeBalancesResponse = {
  workspaceId: string
  branchId: string
  /**
   * The branch's accounting currency — the SCALE every `MoneyText` below is denominated at (IBA-116). Null only
   * if the API somehow omitted it; the client denominates via `useBranchCurrency` then, and never guesses USD.
   */
  currency: CurrencyCode | null
  items: FinanceCurrentIncomeBalanceItem[]
  totals: FinanceCurrentIncomeBalanceTotals
  /** Every ACTIVE account in scope with its ledger balance, and the total across all of them. Never a page. */
  accounts: {
    items: FinanceAccountBalanceItem[]
    totals: { currentBalance: MoneyText }
  }
  categoryAccountBreakdown: FinanceCategoryAccountBreakdown[]
}

export type FinanceCurrentIncomeBalanceResponse = FinanceCurrentIncomeBalanceItem & {
  workspaceId: string
  branchId: string
  /** The branch's accounting currency — the SCALE every `MoneyText` on this payload is denominated at (IBA-116). */
  currency: CurrencyCode | null
  defaultAccountId: string | null
  allowedAccounts: FinanceCurrentIncomeBalanceAccountBreakdown[]
}

export type CreateParishIncomeRequest = {
  workspaceId: string
  branchId?: string
  incomeCategoryId: string
  /** The single destination financial account for this parish income. */
  accountId?: string | null
  /** A canonical decimal string (IBA-116) — money is never sent as a JSON number. */
  amount: MoneyText
  occurredAt?: string
  paymentMethod?: FinancePaymentMethod | null
  paymentReference?: string | null
  description?: string | null
}

export type CreateParishExpenseRequest = {
  workspaceId: string
  branchId?: string
  expenseCategoryId: string
  /** The single source financial account for this parish expense. */
  accountId?: string | null
  /** IBA-154 (D9): the fund this expense draws down. Required — there is no General Fund to fall back on. */
  fundId: string
  /** A canonical decimal string (IBA-116) — money is never sent as a JSON number. */
  amount: MoneyText
  activityId?: string | null
  occurredAt?: string
  paymentMethod?: FinancePaymentMethod | null
  paymentReference?: string | null
  description?: string | null
}

export type ParishTransactionResponse = {
  id: string
  workspaceId: string
  branchId: string
  accountId: string | null
  transactionReference: string | null
  type: TransactionDirection
  amount: MoneyText
  accountingAccountId: string | null
  activityId: string | null
  paymentMethod: FinancePaymentMethod | null
  paymentReference: string | null
  description: string | null
  occurredAt: string
  status: FinanceTransactionStatus
  account: FinanceTransactionAccountSummary | null
  lines: FinanceTransactionLine[]
  accountingAccount: { id: string; name: string; code?: string } | null
  activity: Pick<FinanceTransactionActivitySummary, "id" | "title"> | null
  createdAt: string
  updatedAt: string
}

/** @deprecated Alias of {@link Currency}. Kept so existing call sites keep compiling; prefer `Currency`. */
export const CurrencyCode = Currency
export type CurrencyCode = Currency

export enum ExpenseCategory {
  Utilities = "UTILITIES",
  Supplies = "SUPPLIES",
  Transport = "TRANSPORT",
  Payroll = "PAYROLL",
  Maintenance = "MAINTENANCE",
  Other = "OTHER",
}

export type ListFinanceAccountsQuery = {
  workspaceId: string
  branchId?: string | null
  /** Scope to a department's own accounts (department finance). */
  departmentId?: string
  scope?: FinanceCategoryScope
  status?: FinanceDirectoryStatusFilter
  /** Include each account's ledger balance in the response (for pickers that show "available in account"). */
  includeBalances?: boolean
  search?: string
  page?: number
  limit?: number
  sortBy?: "createdAt" | "updatedAt" | "name"
  sortOrder?: "asc" | "desc"
}

/** Status filter accepted by the funds list endpoint (`ALL` disables the filter). */
export enum FinanceFundListStatus {
  Active = "ACTIVE",
  Closed = "CLOSED",
  Archived = "ARCHIVED",
  All = "ALL",
}

/**
 * List-only KIND filter (IBA-154 D9). Every income account gets a SYSTEM_LINKED fund automatically, so a
 * workspace holds one per income account before anyone defines a fund of their own — which buries the CUSTOM
 * funds the church actually created. `All` is a UI convenience, not a stored kind.
 */
export enum FinanceFundListKind {
  SystemLinked = "SYSTEM_LINKED",
  Custom = "CUSTOM",
  All = "ALL",
}

export type ListFinanceFundsQuery = {
  workspaceId: string
  branchId?: string | null
  scope?: FinanceCategoryScope
  status?: FinanceFundListStatus
  kind?: FinanceFundListKind
  search?: string
  page?: number
  limit?: number
  sortBy?: "createdAt" | "updatedAt" | "name"
  sortOrder?: "asc" | "desc"
}

export type CreateFinanceAccountInput = {
  workspaceId: string
  branchId?: string | null
  /** Optional department ownership (department finance). */
  departmentId?: string
  scope?: FinanceCategoryScope
  name: string
  description?: string | null
  /** Optional last 4 digits of the real account number — a display-only reference aid. */
  accountNumberLast4?: string | null
  type?: FinanceAccountType
  /** A canonical decimal string (IBA-116). May be NEGATIVE — an overdrawn account is a real balance. */
  openingBalance?: MoneyText
}

export type UpdateFinanceAccountInput = {
  id: string
  patch: {
    name?: string
    description?: string | null
    /** Optional last 4 digits of the real account number — a display-only reference aid. */
    accountNumberLast4?: string | null
    type?: FinanceAccountType
    /** A canonical decimal string (IBA-116). May be NEGATIVE — an overdrawn account is a real balance. */
    openingBalance?: MoneyText
  }
}

export type CreateFinanceFundInput = {
  workspaceId: string
  branchId?: string | null
  scope?: FinanceCategoryScope
  name: string
  description?: string | null
  /** A canonical decimal string (IBA-116); `""`/null both mean "no target". */
  targetAmount?: MoneyText | null
  startDate?: string | null
  endDate?: string | null
  isRestricted?: boolean
  restrictionStartDate?: string | null
  restrictionEndDate?: string | null
  allowedExpenseAccountIds?: string[]
  /** Dedicate the new fund to this activity in the same call, so "create a fund for this activity" is one request. */
  dedicateToActivityId?: string
}

export type CreateFinanceSetupFundInput = Pick<
  CreateFinanceFundInput,
  "workspaceId" | "branchId" | "scope" | "name" | "description"
>

/**
 * Reallocation (IBA-154 D9): move a DESIGNATION from one fund to another. No physical money moves and no income
 * history changes — only what the money is for.
 */
export type ReallocateFundsInput = {
  workspaceId: string
  branchId?: string | null
  scope?: FinanceCategoryScope
  sourceFundId: string
  destinationFundId: string
  /** A canonical decimal string (IBA-116) — money is never sent as a JSON number. */
  amount: MoneyText
}

export type UpdateFinanceFundInput = {
  id: string
  patch: {
    name?: string
    description?: string | null
    /** A canonical decimal string (IBA-116); `""`/null both mean "no target". */
    targetAmount?: MoneyText | null
    startDate?: string | null
    endDate?: string | null
    isRestricted?: boolean
    restrictionStartDate?: string | null
    restrictionEndDate?: string | null
    allowedExpenseAccountIds?: string[]
  }
}





/* -------------------------------------------------------------------------- */
/* Cross-context transfers (IBA-158)                                           */
/* -------------------------------------------------------------------------- */

/**
 * Lifecycle of a CROSS-CONTEXT transfer (branch → parish, branch → branch). Mirrors the Prisma enum 1:1.
 *
 * Same-branch account-to-account transfers do not have a lifecycle at all — they post instantly, because
 * there is no receiving side to accept. Only `Approved` means money actually moved.
 */
export enum FinanceTransferStatus {
  /** Prepared, not submitted. Nothing reserved, nothing posted. */
  Draft = "DRAFT",
  /** Awaiting the destination's decision. The source's money is FROZEN but nothing has posted. */
  Pending = "PENDING",
  /** Accepted: two balanced entries posted and the hold consumed. Terminal. */
  Approved = "APPROVED",
  /** Declined by the destination. Hold released; nothing ever posted. Terminal. */
  Rejected = "REJECTED",
  /** Withdrawn by the source before a decision. Hold released. Terminal. */
  Cancelled = "CANCELLED",
}

export type FinanceTransferNamedRef = { id: string; name: string; nameTranslationKey?: string | null } | null

/**
 * A branch as it appears on a transfer.
 *
 * `branchType` is what tells the client which side is the parish. Without it the UI both leaks the
 * internal `SYSTEM_BRANCH_<workspace>` name and asks for BRANCH-scoped accounts and funds against the
 * system branch, which the API rejects outright.
 */
export type FinanceTransferBranchSummary = {
  id: string
  name: string
  slug: string
  branchType: BranchType
}

/** A context a transfer may be addressed to. `branchType` is SYSTEM for the parish. */
export type FinanceTransferDestination = {
  id: string
  name: string
  slug: string
  branchType: string
}

export type FinanceTransferRequestRecord = {
  id: string
  workspaceId: string
  status: FinanceTransferStatus
  transferReference: string | null
  /** Canonical decimal strings (IBA-116) — equal in V1; separate so conversion can land later. */
  sourceAmount: MoneyText
  destinationAmount: MoneyText
  currency: string
  sourceBranchId: string
  destinationBranchId: string
  /** `branchType` distinguishes the SYSTEM (parish) branch — needed for its label and for scoping lookups. */
  sourceBranch: FinanceTransferBranchSummary | null
  destinationBranch: FinanceTransferBranchSummary | null
  sourceAccountId: string
  sourceAccount: FinanceTransferNamedRef
  /** Null until approval — the receiver chooses where the money lands. */
  destinationAccountId: string | null
  destinationAccount: FinanceTransferNamedRef
  sourceFundId: string
  sourceFund: FinanceTransferNamedRef
  destinationFundId: string | null
  destinationFund: FinanceTransferNamedRef
  reference: string | null
  description: string | null
  occurredAt: string
  submittedAt: string | null
  approvedAt: string | null
  rejectedAt: string | null
  rejectionReason: string | null
  cancelledAt: string | null
  cancellationReason: string | null
  sourceTransactionId: string | null
  destinationTransactionId: string | null
  /** Set once an approved transfer has been corrected by a compensating cash reversal (IBA-158 / F8). */
  reversedAt: string | null
  reversalReason: string | null
  /**
   * Every actor in the lifecycle. A transfer is a two-sided agreement, so "who approved this?" is the first
   * question anyone asks of a posted one — and the author alone cannot answer it.
   */
  createdByUser: FinanceTransactionUserSummary | null
  /** Present on both halves of a transfer; null otherwise. */
  transfer?: FinanceTransactionTransferContext | null
  submittedByUser: FinanceTransactionUserSummary | null
  approvedByUser: FinanceTransactionUserSummary | null
  rejectedByUser: FinanceTransactionUserSummary | null
  cancelledByUser: FinanceTransactionUserSummary | null
  createdAt: string
  updatedAt: string
}


/**
 * How a transfer amount was CALCULATED, when it was not typed by hand.
 *
 * The two modes measure genuinely different things. INCOME_ACCOUNT is "what came in through this account";
 * FUND is "what was allocated into this fund during the period" — deliberately not the fund's balance, which
 * also holds money designated in earlier periods.
 */
export enum TransferDerivationMode {
  IncomeAccount = "INCOME_ACCOUNT",
  Fund = "FUND",
}

/** What the calculator produced, plus the advisory duplicate it found (if any). */
export type TransferDerivationResult = {
  mode: TransferDerivationMode
  sourceId: string
  sourceName: string | null
  dateFrom: string
  dateTo: string
  preset: string | null
  percentage: number
  currency: string
  /** The ledger figure the percentage was applied to. */
  baseAmount: MoneyText
  derivedAmount: MoneyText
  /** How many ledger entries the base figure rests on — a total nobody can count is a total nobody can check. */
  entryCount: number
  /** A live transfer already derived from the same source and period. Warning only — never blocks. */
  duplicate: {
    transferId: string
    reference: string | null
    status: string
    amount: MoneyText
    percentage: number | null
    createdAt: string
  } | null
}

/** The criteria stored on a transfer, so the same period is not quietly remitted twice. */
export type TransferDerivationInput = {
  mode: TransferDerivationMode
  sourceId: string
  preset?: string | null
  dateFrom: string
  dateTo: string
  percentage: number
  derivedAmount: MoneyText
}


/** A named, reusable transfer calculation ("Weekly Parish Tithes"). Branch-scoped. */
export type SavedTransferCalculation = {
  id: string
  name: string
  mode: TransferDerivationMode
  sourceId: string
  /** A rolling period key ("last-week"), never fixed dates — see the backend note. */
  preset: string
  percentage: number
  destinationBranchId: string
  destinationBranchName: string
  destinationBranchType: string
  sourceAccountId: string
  sourceAccountName: string
  sourceFundId: string
  sourceFundName: string
  createdAt: string
}

export type CreateSavedTransferCalculationInput = {
  workspaceId: string
  branchId: string
  name: string
  mode: TransferDerivationMode
  sourceId: string
  preset: string
  percentage: number
  destinationBranchId: string
  sourceAccountId: string
  sourceFundId: string
}

export type DeriveTransferAmountQuery = {
  workspaceId: string
  branchId: string
  mode: TransferDerivationMode
  incomeAccountId?: string
  fundId?: string
  dateFrom: string
  dateTo: string
  preset?: string
  percentage: number
  excludeTransferId?: string
}

export type CreateFinanceTransferRequestInput = {
  workspaceId: string
  sourceAccountId: string
  /** Required: money is always designated, and this fund is frozen at submit alongside the account. */
  sourceFundId: string
  destinationBranchId: string
  amount: MoneyText
  occurredAt: string
  reference?: string | null
  description?: string | null
  /** Submit immediately rather than saving a draft. Submitting freezes the money at once. */
  submit?: boolean
  /** Absent for the ordinary manual transfer, which is the default flow. */
  derivation?: TransferDerivationInput | null
}

export type ApproveFinanceTransferRequestInput = {
  id: string
  destinationAccountId: string
  destinationFundId: string
  note?: string | null
}

export type FinanceTransferRequestListQuery = {
  workspaceId: string
  sourceBranchId?: string
  destinationBranchId?: string
  /** Either side. Not the same as setting both, which ANDs into "sent to itself". */
  branchId?: string
  status?: FinanceTransferStatus
  page?: number
  limit?: number
}
